import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icons, Popup } from '@shopline/dashboard-ui';
import { useIntegrate, IntegrateResponse } from '../useIntegrate';
import ToastContext from 'contexts/Toast';
import { nanoid } from 'nanoid';
import { useQueryMerchant } from '../useMerchant';
import { PopupContent } from '../components/PopupContent';
import { useDisintegrate, DisintegrateResponse } from '../useDisintegrate';
import { useQueryInitialState } from 'hooks/useInitialState';
import { enableUnsavedPopup } from '../postMessages';
import { Actions, useConfigurationForm } from 'contexts/ConfigurationForm';
import usePermissions from 'hooks/usePermissions';
import CircleTickIcon from '../components/CircleTickIcon';
import { PRODUCT_API_TYPE } from '../constants';
import {
  IntegrationFormSection,
  IntegrationFormContainer,
  IntegrationFormTitle,
  IntegrationFormDescription,
  GoogleMerchantIdContainer,
  EnhancedWell,
  WellTitle,
  DisintegrateButton,
  EnhancedSuccessAlert,
} from './StyledComponents';

const McaIntegrationForm = () => {
  const { t } = useTranslation(['main', 'common']);
  const { createUIToast } = useContext(ToastContext);
  const [showUnlinkPopup, setShowUnlinkPopup] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const {
    isSuccess: isInitialStateLoaded,
    data: initialState,
    refetch: refetchInitialState,
  } = useQueryInitialState();
  const {
    isSuccess: isMerchantLoaded,
    data: merchant,
    refetch: refetchMerchant,
  } = useQueryMerchant();
  const { dispatch } = useConfigurationForm();
  const { mutate: integrate, isLoading: integrating } = useIntegrate({
    onSuccess: async (_responseData: IntegrateResponse) => {
      const merchantResult = await refetchMerchant();
      createUIToast({
        id: nanoid(),
        type: 'success',
        titleWithParams: {
          key: 'main:A GMC sub-account has been created in your SHOPLINE MCA',
        },
        duration: 5000,
      });
      dispatch(Actions.setCurrentState(merchantResult.data, []));
      dispatch(Actions.setProductApiEnabled(true));
      setShowSuccessAlert(true);
      enableUnsavedPopup({
        popUpTitle: t('Merchant API settings not saved'),
        popUpContent: t(
          'The system will temporarily save your settings as "Not connected to Merchant API".',
        ),
      });
    },
    onError: (error) => {
      console.error(error);
      createUIToast({
        id: nanoid(),
        type: 'alert',
        titleWithParams: {
          key: 'common:Something went wrong. Please try again.',
        },
        duration: 5000,
      });
    },
  });
  const { mutate: disintegrate, isLoading: disintegrating } = useDisintegrate({
    onSuccess: (_responseData: DisintegrateResponse) => {
      setShowUnlinkPopup(false);
      refetchMerchant();
      refetchInitialState();
    },
    onError: (error) => {
      console.error(error);
      setShowUnlinkPopup(false);
      createUIToast({
        id: nanoid(),
        type: 'alert',
        titleWithParams: {
          key: 'common:Something went wrong. Please try again.',
        },
        duration: 5000,
      });
    },
  });
  const canEdit = usePermissions('merchant', 'edit');

  const merchantId = initialState?.merchant?.id;
  const mcaIntegrated =
    merchant?.productApiType === PRODUCT_API_TYPE.MERCHANT_API;

  if (!isInitialStateLoaded || !isMerchantLoaded) {
    return null;
  }
  return (
    <IntegrationFormSection
      title={mcaIntegrated ? t('Merchant Center ID') : null}
    >
      {!mcaIntegrated && (
        <IntegrationFormContainer>
          <div>
            <IntegrationFormTitle>
              {t('Create and connect Google Merchant Center account')}
            </IntegrationFormTitle>
            <IntegrationFormDescription>
              {t(
                'We will help you create a new GMC account using your authorized Google account and automatically connect it to SHOPLINE.',
              )}
            </IntegrationFormDescription>
          </div>
          <div>
            <Button.Primary
              type="button"
              onClick={() => integrate(merchantId)}
              isLoading={integrating}
              disabled={!canEdit}
            >
              {t('Connect now')}
            </Button.Primary>
          </div>
        </IntegrationFormContainer>
      )}
      {mcaIntegrated && (
        <>
          {showSuccessAlert && (
            <EnhancedSuccessAlert
              showCloseButton={true}
              onClose={() => setShowSuccessAlert(false)}
            >
              {t(
                'A new Merchant Center account has been created and connected to your store. Please check your inbox and accept the Google Merchant Center admin invitation.',
              )}
              <ul>
                <li>
                  {t('Website ownership has been transferred to this account.')}
                </li>
                <li>{t('Product data will be synced to this account.')}</li>
              </ul>
            </EnhancedSuccessAlert>
          )}

          <GoogleMerchantIdContainer>
            <WellTitle>{t('Connected Merchant Center ID')}</WellTitle>
            <EnhancedWell>
              <div>
                <CircleTickIcon size="MEDIUM" />
                {merchant?.googleMerchantId}
              </div>
              <div>
                <DisintegrateButton
                  onClick={() => setShowUnlinkPopup(true)}
                  disabled={!canEdit}
                >
                  <Icons.Outline.Unlink size="MEDIUM" />
                  {t('Disconnect')}
                </DisintegrateButton>
              </div>
            </EnhancedWell>
          </GoogleMerchantIdContainer>
          {showUnlinkPopup && (
            <Popup
              width="480px"
              title={t('Disconnect Google Merchant Center account?')}
              showCloseBtn={true}
              onClose={() => setShowUnlinkPopup(false)}
              renderFooter={() => (
                <>
                  <Button.Default
                    height="LARGE"
                    onClick={() => setShowUnlinkPopup(false)}
                  >
                    {t('Cancel', { ns: 'common' })}
                  </Button.Default>
                  <Button.Destructive
                    height="LARGE"
                    onClick={() => disintegrate(merchantId)}
                    isLoading={disintegrating}
                  >
                    {t('Confirm Disconnect')}
                  </Button.Destructive>
                </>
              )}
            >
              <PopupContent>
                {t(
                  'Product data will stop syncing, and any ads linked to this account will be paused. Are you sure you want to disconnect from Google Merchant Center?',
                )}
              </PopupContent>
            </Popup>
          )}
        </>
      )}
    </IntegrationFormSection>
  );
};

export default McaIntegrationForm;
